import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTransactions } from '../../hooks/useTransactions';
import { useTransactions as useYNAB } from '../../hooks/useYNAB';
import { useInsights } from '../../hooks/useInsights';
import { useTrackerSummary } from '../../hooks/useTrackerSummary';
import { useAvailableBalance } from '../../hooks/useAvailableBalance';
import { Card } from '../ui/Card';
import { CategoryIcon } from '../ui/CategoryIcon';
import { format, addMonths, subMonths } from 'date-fns';
import {
  AlertTriangle,
  ArrowDownLeft,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Landmark,
  ShieldCheck,
  Sparkles,
  TrendingDown,
  TrendingUp,
  Wallet,
  WalletCards
} from 'lucide-react';

const currency = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0
});

const formatCurrency = (value) => currency.format(Number(value || 0));
const roundCurrency = (value) => Math.round(Number(value || 0) * 100) / 100;

const getBudgetTone = (amount) => {
  if (amount < 0) {
    return { text: 'text-[#EF4444]', bg: 'bg-[#FEF2F2]', bar: 'bg-[#EF4444]' };
  }
  if (amount === 0) {
    return { text: 'text-[#10B981]', bg: 'bg-[#ECFDF5]', bar: 'bg-[#10B981]' };
  }
  return { text: 'text-[#F59E0B]', bg: 'bg-[#FFFBEB]', bar: 'bg-[#F59E0B]' };
};

const getUtilizationTone = (percentage, balance = 0) => {
  if (balance < 0 || percentage > 100) {
    return { text: 'text-[#EF4444]', bg: 'bg-[#FEF2F2]', bar: 'bg-[#EF4444]' };
  }
  if (percentage >= 80) {
    return { text: 'text-[#F59E0B]', bg: 'bg-[#FFFBEB]', bar: 'bg-[#F59E0B]' };
  }
  return { text: 'text-[#10B981]', bg: 'bg-[#ECFDF5]', bar: 'bg-[#10B981]' };
};

const DEFAULT_YNAB_DATA = {
  budget: null,
  budgetCategories: [],
  totalAllocated: 0,
  totalSpent: 0,
  unallocated: 0,
  toBeBudgeted: 0,
  isZeroBased: false,
  categoryTargets: [],
  ageOfMoney: 0,
  ageOfMoneyStatus: 'no-data',
  daysToAgeGoal: 30,
  loading: false,
  error: null
};

export default function Dashboard({ user }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const { monthlyTransactions, carryForward, salaryThisMonth, expensesThisMonth, loading } = useTransactions(user?.id, currentDate);
  const ynab = useYNAB(user?.id, currentDate);
  const tracker = useTrackerSummary(user?.id);
  const availableBalance = useAvailableBalance(user?.id);

  const ynabData = {
    ...DEFAULT_YNAB_DATA,
    ...(ynab || {})
  };

  const insights = useInsights({
    allTransactions: ynab?.allTransactions || [],
    monthlyTransactions,
    salaryThisMonth,
    expensesThisMonth,
    carryForward,
    ageOfMoney: ynabData.ageOfMoney,
    ageOfMoneyStatus: ynabData.ageOfMoneyStatus,
    totalAllocated: ynabData.totalAllocated,
    toBeBudgeted: ynabData.toBeBudgeted ?? ynabData.unallocated ?? 0,
    budgetCategories: ynabData.budgetCategories,
    selectedDate: currentDate
  });

  const currentBalance = availableBalance.balance;
  const monthlyNetChange = salaryThisMonth - expensesThisMonth;
  const spendRate = salaryThisMonth > 0 ? Math.min((expensesThisMonth / salaryThisMonth) * 100, 100) : 0;
  const remainingRate = salaryThisMonth > 0 ? Math.max((monthlyNetChange / salaryThisMonth) * 100, 0) : 0;
  const toBeBudgeted = roundCurrency(ynabData.toBeBudgeted ?? ynabData.unallocated ?? 0);
  const isBudgetReady = toBeBudgeted === 0;
  const budgetStatusTone = getBudgetTone(toBeBudgeted);
  const spendTone = getUtilizationTone(spendRate, monthlyNetChange);
  const userInitials = user?.email?.slice(0, 2).toUpperCase() || 'SP';

  const hierarchy = useMemo(() => {
    const grouped = monthlyTransactions.reduce((acc, t) => {
      if (t.debit > 0) {
        const cat = t.categories?.name || 'General';
        if (!acc[cat]) acc[cat] = { total: 0, subs: {}, iconSlug: t.categories?.icon_slug };
        acc[cat].total += Number(t.debit);
        const sub = t.subcategories?.name || 'Other';
        acc[cat].subs[sub] = (acc[cat].subs[sub] || 0) + Number(t.debit);
      }
      return acc;
    }, {});

    return Object.entries(grouped)
      .sort(([, a], [, b]) => b.total - a.total)
      .map(([cat, data], index) => ({
        cat,
        total: data.total,
        iconSlug: data.iconSlug,
        subs: Object.entries(data.subs).sort(([, a], [, b]) => b - a),
        tone: ['from-indigo-500 to-sky-400', 'from-rose-500 to-orange-400', 'from-emerald-500 to-teal-400', 'from-amber-500 to-yellow-300', 'from-violet-500 to-fuchsia-400'][index % 5]
      }));
  }, [monthlyTransactions]);

  const recentTransactions = monthlyTransactions.slice(0, 5);
  const weeklyTrend = useMemo(() => {
    const bars = Array.from({ length: 6 }, () => 0);
    monthlyTransactions.forEach((transaction) => {
      if (Number(transaction.debit || 0) <= 0) return;
      const day = new Date(transaction.date).getDate();
      const weekIndex = Math.min(5, Math.floor((day - 1) / 5));
      bars[weekIndex] += Number(transaction.debit || 0);
    });
    const max = Math.max(...bars, 1);
    return bars.map((value) => ({ value, height: Math.max(8, (value / max) * 100) }));
  }, [monthlyTransactions]);

  const insightCards = useMemo(() => {
    return (insights || []).map((insight) => {
      const type = insight.type || 'info';
      const toneMap = {
        success: { title: 'Momentum', icon: CheckCircle2, bg: 'bg-emerald-50 text-emerald-700 border-emerald-100', action: 'Keep going' },
        warning: { title: 'Watchlist', icon: AlertTriangle, bg: 'bg-amber-50 text-amber-700 border-amber-100', action: 'Review now' },
        info: { title: 'Insight', icon: Sparkles, bg: 'bg-sky-50 text-sky-700 border-sky-100', action: 'Take action' }
      };
      const tone = toneMap[type] || toneMap.info;
      return {
        ...insight,
        title: tone.title,
        action: tone.action,
        Icon: tone.icon,
        toneClass: tone.bg
      };
    });
  }, [insights]);

  const healthCards = [
    {
      title: 'Budget status',
      value: isBudgetReady ? 'Healthy' : 'Needs attention',
      note: `${formatCurrency(toBeBudgeted)} left to assign`,
      href: '/budget-allocation',
      icon: WalletCards,
      tone: 'bg-sky-50 text-sky-700'
    },
    {
      title: 'Savings',
      value: formatCurrency(tracker.savings),
      note: 'Progress in the tracker',
      href: '/money-tracker?tab=savings',
      icon: Landmark,
      tone: 'bg-violet-50 text-violet-700'
    },
    {
      title: 'Lent money',
      value: formatCurrency(tracker.lent),
      note: 'Money owed to you',
      href: '/money-tracker?tab=lent',
      icon: Wallet,
      tone: 'bg-amber-50 text-amber-700'
    },
    {
      title: 'Borrowed money',
      value: formatCurrency(tracker.safekeeping),
      note: 'Money you are holding',
      href: '/money-tracker?tab=safekeeping',
      icon: ShieldCheck,
      tone: 'bg-emerald-50 text-emerald-700'
    }
  ];

  if (loading) {
    return (
      <div className="-m-4 min-h-screen bg-slate-50 p-4 md:-m-10 md:p-10">
        <div className="mx-auto max-w-7xl space-y-4">
          <div className="h-40 animate-pulse rounded-[32px] bg-white shadow-sm" />
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="h-56 animate-pulse rounded-[32px] bg-white shadow-sm" />
            <div className="h-56 animate-pulse rounded-[32px] bg-white shadow-sm" />
          </div>
          <div className="h-40 animate-pulse rounded-[32px] bg-white shadow-sm" />
        </div>
      </div>
    );
  }

  return (
    <div className="-m-4 min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.08),_transparent_45%),linear-gradient(180deg,_#f8fbff_0%,_#ffffff_100%)] pb-24 font-sans md:-m-10 md:pb-10">
      <section className="px-4 pb-6 pt-6 md:px-10 md:pt-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.32em] text-slate-500">Home command center</p>
              <h1 className="mt-2 text-2xl font-black tracking-tight text-slate-950 sm:text-3xl">Your money at a glance</h1>
            </div>
            <Link to="/profile" className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-sm font-black text-slate-700 shadow-sm transition hover:border-sky-400 hover:text-sky-600">
              {userInitials}
            </Link>
          </div>

          <div className="mb-4 flex items-center justify-between gap-2 rounded-[24px] border border-slate-200 bg-white/90 p-2 shadow-sm backdrop-blur">
            <button onClick={() => setCurrentDate(subMonths(currentDate, 1))} className="grid h-10 w-10 place-items-center rounded-2xl text-slate-700 transition hover:bg-slate-100" aria-label="Previous month">
              <ChevronLeft size={20} />
            </button>
            <div className="min-w-0 px-2 text-center">
              <p className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-500">Selected month</p>
              <p className="text-sm font-black text-slate-950">{format(currentDate, 'MMMM yyyy')}</p>
            </div>
            <button onClick={() => setCurrentDate(addMonths(currentDate, 1))} className="grid h-10 w-10 place-items-center rounded-2xl text-slate-700 transition hover:bg-slate-100" aria-label="Next month">
              <ChevronRight size={20} />
            </button>
          </div>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <Link to="/statistics" className="group block overflow-hidden rounded-[32px] border border-slate-200 bg-slate-950 p-6 text-white shadow-[0_24px_60px_rgba(15,23,42,0.18)] sm:p-8">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.32em] text-slate-400">Available balance</p>
                  <h2 className="mt-3 text-4xl font-black tracking-tight sm:text-5xl">{availableBalance.loading ? '—' : formatCurrency(currentBalance)}</h2>
                </div>
                <div className="rounded-full border border-white/10 bg-white/10 px-3 py-1 text-sm font-semibold text-slate-200">
                  {monthlyTransactions.length} entries
                </div>
              </div>

              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                <div className="rounded-[22px] border border-white/10 bg-white/10 p-4 backdrop-blur">
                  <p className="text-[10px] font-black uppercase tracking-[0.26em] text-slate-400">Income + carry forward</p>
                  <p className="mt-2 text-lg font-black text-emerald-300">{formatCurrency(carryForward + salaryThisMonth)}</p>
                  <p className="mt-1 text-xs font-semibold text-slate-400">
                    {formatCurrency(salaryThisMonth)} income + {formatCurrency(carryForward)} carried forward
                  </p>
                </div>
                <div className="rounded-[22px] border border-white/10 bg-white/10 p-4 backdrop-blur">
                  <p className="text-[10px] font-black uppercase tracking-[0.26em] text-slate-400">Monthly expenses</p>
                  <p className="mt-2 text-lg font-black text-rose-300">{formatCurrency(expensesThisMonth)}</p>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-3 rounded-[22px] border border-white/10 bg-white/10 p-3 text-sm text-slate-200 backdrop-blur">
                <div className="flex items-center gap-2">
                  <CalendarDays size={16} className="text-sky-300" />
                  <span>{format(currentDate, 'MMM yyyy')}</span>
                </div>
                <div className={`flex items-center gap-2 ${spendTone.text}`}>
                  {monthlyNetChange >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                  <span>{spendRate.toFixed(0)}% spent · {remainingRate.toFixed(0)}% buffer</span>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${budgetStatusTone.bg} ${budgetStatusTone.text}`}>
                  {isBudgetReady ? 'Budget ready' : 'Needs attention'}
                </span>
              </div>
              {availableBalance.error && (
                <p className="mt-3 text-xs font-semibold text-rose-200" role="alert">
                  Could not load available balance: {availableBalance.error}
                </p>
              )}
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="px-4 py-2 md:px-10">
        <div className="mx-auto grid max-w-7xl gap-4 md:grid-cols-2 xl:grid-cols-4">
          {healthCards.map((card) => {
            const Icon = card.icon;
            return (
              <Link key={card.title} to={card.href} className="group rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
                <div className={`mb-4 inline-flex rounded-2xl p-2 ${card.tone}`}>
                  <Icon size={18} />
                </div>
                <p className="text-[11px] font-black uppercase tracking-[0.24em] text-slate-500">{card.title}</p>
                <p className="mt-2 text-xl font-black text-slate-950">{card.value}</p>
                <p className="mt-1 text-sm text-slate-500">{card.note}</p>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="px-4 py-6 md:px-10">
        <div className="mx-auto grid max-w-7xl gap-6 xl:grid-cols-[1.1fr_0.9fr]">
          <Card className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.28em] text-slate-500">Smart insights</p>
                <h3 className="mt-1 text-xl font-black tracking-tight text-slate-950">A quick read on your habits</h3>
              </div>
              <div className="rounded-2xl bg-sky-50 p-2 text-sky-700">
                <Sparkles size={18} />
              </div>
            </div>
            <div className="space-y-3">
              {insightCards.length > 0 ? insightCards.map((insight) => {
                const Icon = insight.Icon;
                return (
                  <div key={insight.id} className={`rounded-[22px] border p-4 ${insight.toneClass}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-2">
                        <div className="mt-0.5 rounded-xl bg-white/70 p-1.5">
                          <Icon size={16} />
                        </div>
                        <div>
                          <p className="text-sm font-black">{insight.title}</p>
                          <p className="mt-1 text-sm leading-5 text-slate-700">{insight.text}</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 text-xs font-semibold uppercase tracking-[0.22em] text-slate-600">Suggested action · {insight.action}</div>
                  </div>
                );
              }) : (
                <div className="rounded-[22px] border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm font-medium text-slate-500">
                  More insights will appear as you log activity.
                </div>
              )}
            </div>
          </Card>

          <Card className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.28em] text-slate-500">Recent activity</p>
                <h3 className="mt-1 text-xl font-black tracking-tight text-slate-950">Latest transactions</h3>
              </div>
              <Link to="/transactions" className="text-sm font-semibold text-sky-700">View all</Link>
            </div>
            <div className="space-y-3">
              {recentTransactions.length === 0 ? (
                <div className="rounded-[22px] border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm font-medium text-slate-500">
                  No recent transactions yet.
                </div>
              ) : recentTransactions.map((transaction) => {
                const isCredit = Number(transaction.credit || 0) > 0;
                const amount = isCredit ? transaction.credit : transaction.debit;
                return (
                  <div key={transaction.id} className="flex items-center gap-3 rounded-[22px] border border-slate-200 bg-slate-50/70 p-3">
                    <div className={`grid h-11 w-11 shrink-0 place-items-center rounded-2xl ${isCredit ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-900 text-white'}`}>
                      {isCredit ? <ArrowDownLeft size={18} /> : <CategoryIcon iconSlug={transaction.categories?.icon_slug} className="h-5 w-5" size={18} />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-black text-slate-900">{transaction.categories?.name || transaction.description || 'Transaction'}</p>
                      <p className="mt-1 text-xs font-medium text-slate-500">{format(new Date(transaction.date), 'dd MMM yyyy')}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-black ${isCredit ? 'text-emerald-600' : 'text-slate-900'}`}>{isCredit ? '+' : '-'}{formatCurrency(amount)}</p>
                      <button type="button" onClick={() => window.location.assign('/form?type=expense')} className="mt-1 text-[11px] font-semibold text-sky-700">Edit</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      </section>

      <section className="px-4 py-2 md:px-10">
        <div className="mx-auto grid max-w-7xl gap-6 xl:grid-cols-[0.95fr_1.05fr]">
          <Card className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.28em] text-slate-500">Budget snapshot</p>
                <h3 className="mt-1 text-xl font-black tracking-tight text-slate-950">Where your plan stands</h3>
              </div>
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${budgetStatusTone.bg} ${budgetStatusTone.text}`}>
                  {isBudgetReady ? 'Ready' : 'Needs attention'}
                </span>
                <Link to="/budget-allocation" className="rounded-full bg-slate-950 px-3 py-1.5 text-sm font-semibold text-white">Open workspace</Link>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-[22px] bg-slate-50 p-4">
                <p className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-500">Allocated</p>
                <p className="mt-2 text-lg font-black text-slate-950">{formatCurrency(ynabData.totalAllocated || 0)}</p>
              </div>
              <div className="rounded-[22px] bg-slate-50 p-4">
                <p className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-500">Remaining</p>
                <p className="mt-2 text-lg font-black text-slate-950">{formatCurrency(Math.max((ynabData.totalAllocated || 0) - (ynabData.totalSpent || 0), 0))}</p>
              </div>
              <div className="rounded-[22px] bg-slate-50 p-4">
                <p className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-500">Completion</p>
                <p className="mt-2 text-lg font-black text-slate-950">{Math.round(((ynabData.totalSpent || 0) / Math.max(ynabData.totalAllocated || 1, 1)) * 100)}%</p>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {(ynabData.budgetCategories || []).slice(0, 5).map((cat) => (
                <div key={cat?.id} className="flex items-center justify-between rounded-[18px] border border-slate-200 bg-white px-3 py-2.5">
                  <span className="text-sm font-semibold text-slate-700">{cat?.category?.name || 'Category'}</span>
                  <span className="text-sm font-black text-slate-950">{formatCurrency(Number(cat?.allocated_amount || 0))}</span>
                </div>
              ))}
              {(!ynabData.budgetCategories || ynabData.budgetCategories.length === 0) && (
                <div className="rounded-[18px] border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-sm font-medium text-slate-500">No budget envelopes yet.</div>
              )}
            </div>
          </Card>

          <Card className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.28em] text-slate-500">Money tracker snapshot</p>
                <h3 className="mt-1 text-xl font-black tracking-tight text-slate-950">What is still in motion</h3>
              </div>
              <Link to="/money-tracker" className="rounded-full bg-slate-950 px-3 py-1.5 text-sm font-semibold text-white">Open tracker</Link>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-[22px] border border-slate-200 bg-slate-50 p-4">
                <p className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-500">Lent money</p>
                <p className="mt-2 text-xl font-black text-slate-950">{formatCurrency(tracker.lent)}</p>
              </div>
              <div className="rounded-[22px] border border-slate-200 bg-slate-50 p-4">
                <p className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-500">Borrowed money</p>
                <p className="mt-2 text-xl font-black text-slate-950">{formatCurrency(tracker.safekeeping)}</p>
              </div>
              <div className="rounded-[22px] border border-slate-200 bg-slate-50 p-4">
                <p className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-500">Savings</p>
                <p className="mt-2 text-xl font-black text-slate-950">{formatCurrency(tracker.savings)}</p>
              </div>
              <div className="rounded-[22px] border border-slate-200 bg-slate-50 p-4">
                <p className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-500">Outstanding amount</p>
                <p className="mt-2 text-xl font-black text-slate-950">{formatCurrency(tracker.lent + tracker.safekeeping + tracker.savings)}</p>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
              <span>Open items ready for follow-up</span>
              <span className="font-black text-slate-950">{tracker.openItems}</span>
            </div>
          </Card>
        </div>
      </section>

      <section className="px-4 py-6 md:px-10">
        <div className="mx-auto grid max-w-7xl gap-6 xl:grid-cols-[0.9fr_1.1fr]">
          <Card className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.28em] text-slate-500">Monthly spending preview</p>
                <h3 className="mt-1 text-xl font-black tracking-tight text-slate-950">Top spending categories</h3>
              </div>
              <Link to="/statistics" className="rounded-full bg-slate-950 px-3 py-1.5 text-sm font-semibold text-white">View full insights</Link>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex h-32 w-32 shrink-0 items-center justify-center">
                <svg viewBox="0 0 120 120" className="h-28 w-28 -rotate-90">
                  <circle cx="60" cy="60" r="44" stroke="#e2e8f0" strokeWidth="16" fill="none" />
                  {hierarchy.slice(0, 3).reduce((acc, category, index) => {
                    const radius = 44;
                    const circumference = 2 * Math.PI * radius;
                    const segment = hierarchy[0] && expensesThisMonth > 0 ? Math.max(10, (category.total / expensesThisMonth) * 100) : 0;
                    const stroke = index === 0 ? '#0f172a' : index === 1 ? '#3b82f6' : '#10b981';
                    const offset = acc.cumulative;
                    acc.cumulative += (segment / 100) * circumference;
                    acc.elements.push(<circle key={category.cat} cx="60" cy="60" r={radius} stroke={stroke} strokeWidth="16" fill="none" strokeDasharray={`${(segment / 100) * circumference} ${circumference}`} strokeDashoffset={-offset} strokeLinecap="round" />);
                    return acc;
                  }, { cumulative: 0, elements: [] }).elements}
                </svg>
              </div>
              <div className="flex-1 space-y-2">
                {hierarchy.slice(0, 3).map((category, index) => (
                  <div key={category.cat} className="flex items-center justify-between gap-2 rounded-[18px] bg-slate-50 px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className={`h-2.5 w-2.5 rounded-full ${index === 0 ? 'bg-slate-950' : index === 1 ? 'bg-sky-500' : 'bg-emerald-500'}`} />
                      <span className="text-sm font-semibold text-slate-700">{category.cat}</span>
                    </div>
                    <span className="text-sm font-black text-slate-950">{formatCurrency(category.total)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-5 flex items-end gap-2 rounded-[24px] border border-slate-200 bg-slate-50 p-3">
              {weeklyTrend.map((bar, index) => (
                <div key={index} className="flex flex-1 flex-col items-center gap-2">
                  <div className="w-full rounded-full bg-white p-1">
                    <div className="rounded-full bg-sky-500" style={{ height: `${bar.height}%`, minHeight: '8px' }} />
                  </div>
                  <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">W{index + 1}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.28em] text-slate-500">Wellness reminders</p>
                <h3 className="mt-1 text-xl font-black tracking-tight text-slate-950">Stay consistent without extra friction</h3>
              </div>
            </div>
            <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-5">
              <p className="text-sm font-semibold text-slate-600">Hydration and wellness nudges now live in your profile settings so the experience stays focused and calm.</p>
              <Link to="/profile" className="mt-5 inline-flex rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white">Manage settings</Link>
            </div>
          </Card>
        </div>
      </section>

    </div>
  );
}
